import axios, { AxiosError, AxiosHeaders } from 'axios';
import i18n from 'i18next';

import { LOCAL_STORAGE_KEY } from '@common/constants';
import { ApiUrl } from '@common/enums';
import { NotifierType, showNotification } from '@core/components';
import { ApiErrorResponse } from '@core/hooks/useEstimateSaver';
import { fetchRetailerApiSettings } from '@ee-api/lookup-retailer-api/lookupRetailerApiService';

export enum PDFType {
    INTERNAL = 'internal',
    RETAIL = 'retail',
}

export const downloadPdf = async ({
    code,
    filename,
    pdfType,
    userToken,
    password,
}: {
    code: string;
    filename: string;
    pdfType: PDFType;
    userToken?: string;
    password?: string;
}): Promise<string | void> => {
    const isInternal = pdfType === PDFType.INTERNAL;
    const params = new URLSearchParams({ [LOCAL_STORAGE_KEY.CODE]: code, [LOCAL_STORAGE_KEY.PDF_TYPE]: pdfType });
    if (isInternal) {
        if (userToken) {
            params.set(LOCAL_STORAGE_KEY.USER_TOKEN, userToken);
        }
        if (password) {
            params.set(LOCAL_STORAGE_KEY.PASSWORD, password);
        }
    }
    const apiLookup = await fetchRetailerApiSettings();

    const { apiHostname: baseUrl, token: bearerToken } = apiLookup;
    const headers = new AxiosHeaders();

    if (bearerToken) {
        headers.set('Authorization', `Bearer ${bearerToken}`);
    }

    try {
        const response = await axios.get(ApiUrl.PDF_URL, {
            responseType: 'blob', // important
            baseURL: baseUrl,
            headers,
            params,
        });

        // Create a blob object from the response data
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });

        // Create a link element, hide it, direct it towards the blob, and then 'click' it programatically
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link?.parentNode?.removeChild(link);
    } catch (e) {
        const error = e as AxiosError;
        const errorResponseBlob = error.response?.data as Blob; // we were expecting a blob, so the response is blobby
        const errorResponseData = JSON.parse(await errorResponseBlob.text()) as ApiErrorResponse;
        showNotification({
            type: NotifierType.ERROR,
            title: errorResponseData.errorCode ?? i18n.t('NOTIFICATIONS.PASSWORD.ERROR.TITLE'),
            description: errorResponseData.errorMessage ?? i18n.t('NOTIFICATIONS.PASSWORD.ERROR.DESC'),
            message: i18n.t('NOTIFICATIONS.PASSWORD.ERROR.MSG'),
            duration: 10,
        });
        throw errorResponseData;
    }
};
